<template>
  <div class="adjustment-draft-list">
    <KTCodePreview title="Danh sách hóa đơn điều chỉnh">
      <template v-slot:toolbar>
        <b-button
          v-if="checkPermission('EINVOICE_ADJUSTMENT_INSERT')"
          size="sm"
          variant="primary"
          @click="goToRefundBills"
        >
          <i class="fas fa-plus mr-1"></i> Tạo mới
        </b-button>
      </template>

      <template v-slot:preview>
        <!-- Filter -->
        <div class="einvoice-filter mb-4">
          <b-row class="align-items-end">
            <b-col md="2" class="mb-3">
              <label class="einvoice-filter__label">Trạng thái</label>
              <b-form-select
                v-model="filter.status"
                class="select-style"
                size="sm"
                :options="statusOptions"
              />
            </b-col>
            <b-col md="3" class="mb-3">
              <label class="einvoice-filter__label">Chi nhánh</label>
              <BDropdownCustom
                :value="selectedStores"
                :options="branches"
                :placeholder="'chi nhánh'"
                :searchable="true"
                :matchKey="['name', 'shortName']"
                :limit="10"
                :compareById="true"
                :multiple="true"
                @valueChange="onStoreSelectionChange"
              />
            </b-col>
            <b-col md="2" class="mb-3">
              <label class="einvoice-filter__label">Luồng</label>
              <b-form-select
                v-model="filter.providerFlow"
                class="select-style"
                size="sm"
                :options="flowOptions"
              />
            </b-col>
            <b-col md="2" class="mb-3">
              <label class="einvoice-filter__label">MST người mua</label>
              <b-form-input
                v-model="filter.buyerTaxCode"
                size="sm"
                maxlength="24"
                placeholder="Nhập MST..."
                @keyup.enter="onFilter"
              />
            </b-col>
            <b-col cols="12" md="3" class="mb-3">
              <label class="einvoice-filter__label">Từ khóa</label>
              <b-input-group size="sm">
                <b-input-group-prepend>
                  <b-input-group-text><i class="fas fa-search"></i></b-input-group-text>
                </b-input-group-prepend>
                <b-form-input
                  v-model="filter.keyword"
                  placeholder="Số HĐĐC, tên khách..."
                  @keyup.enter="onFilter"
                />
              </b-input-group>
            </b-col>
          </b-row>
          <b-row class="align-items-end">

          <b-col md="2" class="mb-3">
              <label class="einvoice-filter__label">Số HĐ trả</label>
              <b-form-input
                v-model="filter.returnBillNumber"
                size="sm"
                placeholder="VD: 10218"
                @keyup.enter="onFilter"
              />
            </b-col>
            <b-col md="2" class="mb-3">
              <label class="einvoice-filter__label">Số HĐ bán gốc</label>
              <b-form-input
                v-model="filter.originalBillNumber"
                size="sm"
                placeholder="Số hóa đơn bán..."
                @keyup.enter="onFilter"
              />
            </b-col>
            <b-col cols="6" md="2" class="mb-3">
              <label class="einvoice-filter__label">Ngày tạo HĐ trả từ</label>
              <date-picker
                v-model="filter.billRefundCreatedAtFrom"
                placeholder="Từ ngày"
                class="form-control form-control-sm"
                :config="dpConfigs.date"
                @input.native="onDateNativeInput('billRefundCreatedAtFrom', $event)"
              />
            </b-col>
            <b-col cols="6" md="2" class="mb-3">
              <label class="einvoice-filter__label">Ngày tạo HĐ trả đến</label>
              <date-picker
                v-model="filter.billRefundCreatedAtTo"
                placeholder="Đến ngày"
                class="form-control form-control-sm"
                :config="dpConfigs.date"
                @input.native="onDateNativeInput('billRefundCreatedAtTo', $event)"
              />
              <small v-if="isBillCreatedAtRangeInvalid" class="d-block text-danger mt-1">
                Ngày không hợp lệ
              </small>
            </b-col>
            <b-col cols="6" md="2" class="mb-3">
              <label class="einvoice-filter__label">Phát hành từ</label>
              <date-picker
                v-model="filter.issueDateFrom"
                placeholder="Từ ngày"
                class="form-control form-control-sm"
                :config="dpConfigs.date"
                @input.native="onDateNativeInput('issueDateFrom', $event)"
              />
            </b-col>
            <b-col cols="6" md="2" class="mb-3">
              <label class="einvoice-filter__label">Phát hành đến</label>
              <date-picker
                v-model="filter.issueDateTo"
                placeholder="Đến ngày"
                class="form-control form-control-sm"
                :config="dpConfigs.date"
                @input.native="onDateNativeInput('issueDateTo', $event)"
              />
              <small v-if="isIssueDateRangeInvalid" class="d-block text-danger mt-1">
                Ngày không hợp lệ
              </small>
            </b-col>
          </b-row>
          <b-row class="align-items-end">
            <b-col cols="12" md="4" class="mb-3">
              <div class="einvoice-filter__actions mt-md-1">
                <b-button size="sm" variant="primary" :disabled="isIssueDateRangeInvalid || isBillCreatedAtRangeInvalid" @click="onFilter">
                  Áp dụng
                </b-button>
                <b-button size="sm" variant="outline-secondary" @click="resetFilter">
                  <i class="fas fa-undo mr-1"></i> Đặt lại
                </b-button>
              </div>
            </b-col>
          </b-row>
        </div>

        <div class="d-flex justify-content-between align-items-center mb-2">
          <p class="text-dark m-0" style="font-weight:500">
            Tổng số: <strong>{{ totalItems }}</strong>
          </p>
          <b-pagination-nav
            pills size="sm"
            v-show="totalPages >= 2"
            :number-of-pages="totalPages"
            :link-gen="linkGen"
            align="right"
            v-model="page"
            use-router
            @change="fetchList"
          />
        </div>

        <BulkActionBar
          v-if="hasSelection"
          :selected-count="selectionCount"
          :is-loading="false"
          :draft-count="draftCountInSelectionForDelete"
          :is-delete-loading="isBulkDeleteProcessing"
          @bulk-issue="triggerBulkIssue"
          @clear-selection="clearSelection"
          @bulk-delete="triggerBulkDelete"
        />

        <!-- Table -->
        <b-skeleton-wrapper :loading="isLoading">
          <template #loading>
            <b-skeleton-table
              :rows="10"
              :columns="11"
              :table-props="{ bordered: true, striped: true }"
            ></b-skeleton-table>
          </template>
          <b-table
            ref="draftTable"
            bordered
            hover
            :items="drafts"
            :fields="tableFields"
            :busy="isLoading"
            show-empty
            empty-text="Không có dữ liệu"
            class="adjustment-draft-btable"
            table-class="einvoice-table adjustment-draft-list__table"
          >
            <template #cell(_selected)="{ item }">
              <b-form-checkbox
                v-model="selectedRows"
                :value="item"
                :disabled="!isRowSelectableForBulk(item)"
                @click.native.stop
              />
            </template>
            <template #head(_selected)>
              <b-form-checkbox
                :checked="isAllSelected"
                :indeterminate="isIndeterminate"
                :disabled="!selectableRows.length"
                @change="toggleSelectAll"
                @click.native.stop
              />
            </template>
            <template #head(adjustmentInvoiceNo)>
              <div class="text-center adjustment-draft-col-head-split">
                <div>Số HĐĐC</div>
                <div class="text-muted">HĐ gốc</div>
              </div>
            </template>
            <!-- Columns -->
            <template #cell(adjustmentInvoiceNo)="{ item }">
              <div class="d-flex flex-column align-items-center adjustment-draft-invoice-cell">
                <span @click="viewDetail(item)" class="adjustment-draft-invoice-link font-weight-bold hover-link text-primary">
                  {{ displayAdjustmentInvoiceNo(item) }}
                </span>
                <hr class="w-100 my-1" style="border-top: 1px solid #bbb">
                <small
                  v-if="originalInvoiceSummary(item)"
                  class="d-block text-center"
                  style="font-size: 0.8rem; line-height: 1.25"
                >
                  <a
                    v-if="getOriginalEinvoiceDetailHref(item)"
                    :href="getOriginalEinvoiceDetailHref(item)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="einvoice-source-link text-dark font-weight-bold"
                    v-b-tooltip.hover
                    title="Mở chi tiết HĐĐT gốc (tab mới)"
                    @click.stop
                  >
                    {{ originalInvoiceSummary(item) }}
                  </a>
                  <span
                    v-else
                    v-b-tooltip.hover
                    title="Số hóa đơn gốc bị điều chỉnh."
                    class="text-muted text-dark font-weight-bold"
                    style="cursor: help"
                  >
                    {{ originalInvoiceSummary(item) }}
                  </span>
                </small>
                <small
                class="text-muted"
                >
                  {{item.displayOriginalInvoicePattern}} / {{item.displayOriginalInvoiceSerial}}
                </small>
              </div>
            </template>
            <template #cell(returnBillNumber)="{ item }">
              <div class="d-flex align-items-center">
                <a
                  :href="getReturnBillDetailHref(item)"
                  class="einvoice-source-link font-weight-bold"
                   v-b-tooltip.hover title="Mở hoá đơn trả hàng"
                >
                  {{ item.returnBillNumber || item.returnBillId }}
                </a>
                <a
                  v-if="getReturnBillDetailHref(item)"
                  :href="getReturnBillDetailHref(item)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="ml-1 text-muted"
                  style="font-size:0.75rem"
                  v-b-tooltip.hover
                  title="Mở hoá đơn trả hàng"
                  @click.stop
                >
                  <i class="fas fa-external-link-alt" style="font-size:0.65rem"></i>
                </a>
              </div>
              <div
                v-if="item.originalBillNumber"
                class="text-muted"
                style="font-size:0.8rem"
              >
                <template v-if="getOriginalBillDetailHref(item)">
                  <a
                    :href="getOriginalBillDetailHref(item)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="einvoice-source-link"
                    v-b-tooltip.hover title="Mở hoá đơn gốc"
                    @click.stop
                  >
                    ← {{ item.originalBillNumber || item.originalBillId }}
                    <i class="fas fa-external-link-alt ml-1" style="font-size:0.65rem"></i>
                  </a>
                </template>
              </div>
            </template>
            <template #cell(store)="{ item }">
              <b>{{ item.storeName || '—' }}</b>
            </template>
            <template #cell(buyer)="{ item }">
              <div>{{ item.buyerLegalName || item.buyerName || '—' }}</div>
              <div class="text-muted" style="font-size:0.8rem" v-if="item.buyerTaxCode">MST: {{ item.buyerTaxCode }} <br/></div>
              <div class="text-muted" style="font-size:0.8rem" v-if="item.buyerEmail">Email: {{ item.buyerEmail }}</div>
            </template>
            <template #cell(providerFlow)="{ item }">
              <span class="badge" :class="item.providerFlow === 'POS' ? 'badge-dark' : 'badge-info'">
                {{ item.providerFlow }}
              </span>
            </template>
            <template #cell(status)="{ item }">
              <div class="d-flex flex-column align-items-center">
                <AdjustmentStatusBadge
                  :status="item.status"
                  :error-message="item.errorMessage"
                  :error-code="item.errorCode"
                />
                <small
                  v-if="item.taxStatus === 'TAX_ACCEPTED'"
                  class="einvoice-text-meta mt-1 text-center text-success"
                >CQT chấp nhận</small>
                <small
                  v-else-if="item.taxStatus === 'TAX_REJECTED'"
                  class="einvoice-text-meta mt-1 text-center text-danger"
                  v-b-tooltip.hover
                  :title="item.taxFeedbackContent || 'CQT từ chối'"
                >CQT từ chối</small>
                <small
                  v-else-if="item.taxStatus === 'TAX_PENDING'"
                  class="einvoice-text-meta mt-1 text-center text-warning"
                >Chờ CQT</small>
              </div>
            </template>
            <template #cell(taxStatus)="{ item }">
              <span
                v-if="item.taxStatus === 'TAX_ACCEPTED'"
                class="text-success"
                v-b-tooltip.hover
                title="CQT đã chấp nhận"
              ><i class="fas fa-check-circle"></i></span>
              <span
                v-else-if="item.taxStatus === 'TAX_REJECTED'"
                class="text-danger"
                v-b-tooltip.hover
                :title="item.taxFeedbackContent || 'CQT từ chối'"
              ><i class="fas fa-times-circle"></i></span>
              <span
                v-else-if="item.taxStatus === 'TAX_PENDING'"
                class="text-warning"
                v-b-tooltip.hover
                title="Đang chờ CQT xử lý"
              ><i class="fas fa-clock"></i></span>
              <span
                v-else
                class="text-muted"
              ><i class="fas fa-minus"></i></span>
            </template>
            <template #cell(fastSyncStatus)="{ item }">
              <AdjustmentStatusBadge
                v-if="item.fastSyncStatus"
                :status="item.fastSyncStatus"
                :error-message="item.fastSyncErrorMessage"
                :error-code="item.fastSyncErrorCode"
              />
              <span v-else class="text-muted">—</span>
            </template>
            <template #cell(totalAmount)="{ item }">
              {{ convertPrice(item.totalAmount) }}
            </template>
            <template #cell(billCreatedAt)="{ item }">
              <span style="font-size: 0.8rem">{{ formatIssuedAt(item.billCreatedAt) }}</span>
            </template>
            <template #cell(issuedAt)="{ item }">
              <span style="font-size: 0.8rem">{{ formatIssuedAt(item.issuedAt) }}</span>
            </template>
            <template #cell(actions)="{ item }">
              <b-dropdown size="sm" variant="link" no-caret right>
                <template #button-content>
                  <i class="fas fa-ellipsis-v text-dark"></i>
                </template>
                <b-dropdown-item @click.stop="viewDetail(item)">
                  <i class="fas fa-eye mr-2"></i> Chi tiết
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="item.actions && item.actions.canIssue"
                  @click.stop="confirmIssue(item)"
                >
                  <i class="fas fa-paper-plane mr-2 text-success"></i> Phát hành
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="item.actions && item.actions.canRetry"
                  @click.stop="confirmRetry(item)"
                >
                  <i class="fas fa-redo mr-2 text-warning"></i> Thử lại
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="item.actions && !item.actions.canRetry && ['ISSUE_FAILED', 'WAITING_PROVIDER'].includes(item.status)"
                  disabled
                >
                  <i class="fas fa-redo mr-2 text-muted"></i> <span class="text-muted">Thử lại (đã hết lượt)</span>
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="item.actions && item.actions.canDownloadPdf"
                  @click.stop="openPdfViewer(item)"
                >
                  <i class="fas fa-file-pdf mr-2 text-danger"></i> Xem PDF
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="item.actions && item.actions.canQueryTaxStatus"
                  @click.stop="refreshTaxStatus(item)"
                >
                  <i class="fas fa-sync-alt mr-2 text-info"></i> Kiểm tra CQT
                </b-dropdown-item>
                <b-dropdown-divider v-if="item.actions && item.actions.canDelete"></b-dropdown-divider>
                <b-dropdown-item
                  v-if="item.actions && item.actions.canDelete"
                  @click.stop="confirmDelete(item)"
                >
                  <i class="fas fa-trash-alt mr-2 text-danger"></i> Xóa
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </b-table>
        </b-skeleton-wrapper>

        <div class="d-flex justify-content-end mt-2">
          <b-pagination-nav
            pills size="sm"
            v-show="totalPages >= 2"
            :number-of-pages="totalPages"
            :link-gen="linkGen"
            align="right"
            v-model="page"
            use-router
            @change="fetchList"
          />
        </div>

        <AdjustmentBulkIssueModal ref="adjustmentBulkIssueModal" @closed="onBulkIssueClosed" />
        <PdfViewerModal ref="pdfViewer" />
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import BDropdownCustom from '@/view/base/bootstrap/BDropdownCustom.vue';
import commonMixin from '@/view/mixins/api-common.mixins';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import { makeToastSuccess, makeToastFaile, convertPrice } from '@/utils/common';
import Swal from 'sweetalert2';
import timeUtils from '@/utils/date';
import BulkActionBar from '../components/BulkActionBar.vue';
import AdjustmentStatusBadge from './components/AdjustmentStatusBadge.vue';
import AdjustmentBulkIssueModal from './components/AdjustmentBulkIssueModal.vue';
import PdfViewerModal from '@/view/components/pdf-viewer/PdfViewerModal.vue';
import { buildAdjustmentDraftPdfViewerConfig } from './utils/adjustmentPdfViewer.utils';
import { formatFilterDate, isFilterDateRangeInvalid } from '../utils/filterForm.utils';
import useAdjustmentDraftBulkSelection from './mixins/useAdjustmentDraftBulkSelection';
import permissionMixin from '@/view/mixins/permission.mixin';
import { EINVOICE_SALE_TYPE } from '@/utils/einvoice.enum';

export default {
  name: 'AdjustmentDraftList',
  components: {
    KTCodePreview,
    BDropdownCustom,
    BulkActionBar,
    AdjustmentStatusBadge,
    AdjustmentBulkIssueModal,
    PdfViewerModal,
  },
  mixins: [commonMixin, useAdjustmentDraftBulkSelection, permissionMixin],
  data() {
    return {
      isBulkDeleteProcessing: false,
      isLoading: false,
      drafts: [],
      totalItems: 0,
      totalPages: 0,
      page: 1,
      branches: [],
      selectedStores: [],
      filter: {
        keyword: '',
        returnBillNumber: '',
        originalBillNumber: '',
        status: '',
        providerFlow: '',
        buyerTaxCode: '',
        storeIds: [],
        billRefundCreatedAtFrom: null,
        billRefundCreatedAtTo: null,
        issueDateFrom: null,
        issueDateTo: null,
      },
      appliedFilter: {},
      dpConfigs: {
        date: {
          ...timeUtils.DP_CONFIG.date,
          widgetPositioning: { horizontal: 'auto', vertical: 'auto' },
        },
      },
    };
  },
  computed: {
    isIssueDateRangeInvalid() {
      return isFilterDateRangeInvalid(this.filter.issueDateFrom, this.filter.issueDateTo);
    },
    isBillCreatedAtRangeInvalid() {
      return isFilterDateRangeInvalid(this.filter.billRefundCreatedAtFrom, this.filter.billRefundCreatedAtTo);
    },
    tableFields() {
      return [
        {
          key: '_selected',
          label: '',
          thStyle: { width: '40px' },
          tdClass: 'text-center',
          sortable: false,
        },
        {
          key: 'adjustmentInvoiceNo',
          label: '',
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { width: '80px', maxWidth: '80px' },
          tdStyle: { width: '80px', maxWidth: '80px' },
        },
        { key: 'returnBillNumber', label: 'HĐ trả' },
        {
          key: 'store',
          label: 'Chi nhánh',
          thClass: 'text-center',
          thStyle: { minWidth: '120px' },
        },
        { key: 'buyer', label: 'Khách hàng',  thStyle: { minWidth: '160px' }, },
        { key: 'totalAmount', label: 'Tổng ĐC (VNĐ)', thClass: 'text-right', tdClass: 'text-right text-danger font-weight-bold', thStyle: { width: '120px' } },
        { key: 'billCreatedAt', label: 'Ngày HĐ', thClass: 'text-center', tdClass: 'text-center', thStyle: { width: '110px' } },
        { key: 'issuedAt', label: 'Ngày phát hành', thClass: 'text-center', tdClass: 'text-center', thStyle: { width: '110px' } },
        { key: 'status', label: 'Trạng thái', thClass: 'text-center', tdClass: 'text-center', thStyle: { width: '130px' } },
        { key: 'taxStatus', label: 'CQT', thClass: 'text-center', tdClass: 'text-center', thStyle: { width: '60px' } },
        { key: 'actions', label: '', thClass: 'text-center', tdClass: 'text-center', thStyle: { width: '70px' } },
      ];
    },
    statusOptions() {
      return [
        { value: '', text: 'Tất cả trạng thái' },
        { value: 'DRAFT', text: 'Nháp' },
        { value: 'ISSUED', text: 'Đã phát hành' },
        { value: 'WAITING_PROVIDER', text: 'Chờ nhà cung cấp' },
        { value: 'ISSUE_FAILED', text: 'Lỗi phát hành' },
        {
          label: 'Trạng thái CQT',
          options: [
            { value: 'TAX_PENDING', text: 'CQT đang xử lý' },
            { value: 'TAX_ACCEPTED', text: 'CQT chấp nhận' },
            { value: 'TAX_REJECTED', text: 'CQT từ chối' },
          ],
        },
      ];
    },
    flowOptions() {
      return [
        { value: '', text: 'Tất cả luồng' },
        { value: 'POS', text: 'POS' },
        { value: 'PORTAL', text: 'Cổng thuế' },
      ];
    },
  },
  async mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Hóa đơn điện tử', route: 'einvoice-list' },
      { title: 'Nháp điều chỉnh GTGT' },
    ]);
    await this.fetchBranches();
    this.syncSelectedStoresFromFilter();
    this.fetchList();
  },
  methods: {
    convertPrice,
    /** Nhãn nhận diện nháp (xóa / toast) — ưu số HĐ điều chỉnh, không thì mã nháp */
    draftShortLabel(item) {
      if (item?.returnBillNumber) return item.returnBillNumber;
      const raw = item?.adjustmentInvoiceNo;
      if (raw != null && String(raw).trim() !== '') return String(raw).trim();
      return item?.draftCode || (item?.id != null ? `#${item.id}` : '—');
    },
    /** Cột số HĐ điều chỉnh — chưa phát hành thì copy cố định */
    displayAdjustmentInvoiceNo(item) {
      const raw = item?.adjustmentInvoiceNo;
      if (raw != null && String(raw).trim() !== '') {
        return String(raw).trim();
      }
      return '—';
    },
    /** Số / ký hiệu HĐ gốc bị điều chỉnh — ưu technical, kế display/prefill, cuối mẫu+ký hiệu */
    originalInvoiceSummary(item) {
      if (!item) return '';
      const trim = (v) => (v != null && String(v).trim() !== '' ? String(v).trim() : '');
      const techNo = trim(item.technicalOriginalInvoiceNo);
      if (techNo) return techNo;
      const dispNo = trim(item.displayOriginalInvoiceNo);
      if (dispNo) return dispNo;
      const preNo = trim(item.prefillOriginalInvoiceNo);
      if (preNo) return preNo;
      const p =
        trim(item.displayOriginalInvoicePattern) ||
        trim(item.prefillOriginalInvoicePattern) ||
        trim(item.technicalOriginalInvoicePattern);
      const s =
        trim(item.displayOriginalInvoiceSerial) ||
        trim(item.prefillOriginalInvoiceSerial) ||
        trim(item.technicalOriginalInvoiceSerial);
      if (p && s) return `${p} / ${s}`;
      if (s) return s;
      if (p) return p;
      return '';
    },
    linkGen: (pageNum) => (pageNum === 1 ? '?' : `?page=${pageNum}`),
    formatIssuedAt(d) {
      if (!d) return '—';
      return timeUtils.convertToDateSlash(d, timeUtils.DATE_FORMAT) || '—';
    },
    onDateNativeInput(field, event) {
      const raw = event?.target?.value;
      if (raw === '' || raw === null || raw === undefined) {
        if (this.filter[field]) {
          this.filter[field] = null;
        }
      }
    },
    async fetchBranches() {
      try {
        const result = await this.fetchStoreByUser();
        this.branches = result.stores || [];
      } catch {
        this.branches = [];
      }
    },
    syncSelectedStoresFromFilter() {
      const storeIds = this.filter.storeIds || [];
      if (!storeIds.length || !this.branches.length) {
        this.selectedStores = [];
        return;
      }
      this.selectedStores = this.branches.filter((s) => s && storeIds.includes(s.id));
    },
    onStoreSelectionChange(stores) {
      const selected = Array.isArray(stores) ? stores.filter(Boolean) : [];
      this.selectedStores = selected;
      this.filter.storeIds = selected.map((s) => s.id);
    },
    onFilter() {
      if (this.isIssueDateRangeInvalid || this.isBillCreatedAtRangeInvalid) return;
      this.clearSelection();
      this.page = 1;
      const buyerTaxCode =
        typeof this.filter.buyerTaxCode === 'string' && this.filter.buyerTaxCode.trim()
          ? this.filter.buyerTaxCode.trim()
          : undefined;
      const storeIds =
        Array.isArray(this.filter.storeIds) && this.filter.storeIds.length
          ? [...this.filter.storeIds]
          : undefined;
      const returnBillNumber =
        typeof this.filter.returnBillNumber === 'string' && this.filter.returnBillNumber.trim()
          ? this.filter.returnBillNumber.trim()
          : undefined;
      const originalBillNumber =
        typeof this.filter.originalBillNumber === 'string' && this.filter.originalBillNumber.trim()
          ? this.filter.originalBillNumber.trim()
          : undefined;
      const CQT_STATUSES = ['TAX_PENDING', 'TAX_ACCEPTED', 'TAX_REJECTED'];
      const isCqtStatus = CQT_STATUSES.includes(this.filter.status);
      this.appliedFilter = {
        keyword: this.filter.keyword,
        returnBillNumber,
        originalBillNumber,
        status: isCqtStatus ? undefined : this.filter.status || undefined,
        taxStatus: isCqtStatus ? this.filter.status : undefined,
        providerFlow: this.filter.providerFlow,
        buyerTaxCode,
        storeIds,
        billRefundCreatedAtFrom: formatFilterDate(this.filter.billRefundCreatedAtFrom),
        billRefundCreatedAtTo: formatFilterDate(this.filter.billRefundCreatedAtTo),
        issueDateFrom: formatFilterDate(this.filter.issueDateFrom),
        issueDateTo: formatFilterDate(this.filter.issueDateTo),
      };
      this.fetchList();
    },
    resetFilter() {
      this.clearSelection();
      this.filter = {
        keyword: '',
        returnBillNumber: '',
        originalBillNumber: '',
        status: '',
        providerFlow: '',
        buyerTaxCode: '',
        storeIds: [],
        billRefundCreatedAtFrom: null,
        billRefundCreatedAtTo: null,
        issueDateFrom: null,
        issueDateTo: null,
      };
      this.selectedStores = [];
      this.page = 1;
      this.appliedFilter = {};
      this.fetchList();
    },
    async fetchList() {
      this.page = Number(this.$route.query.page) || this.page || 1;
      this.isLoading = true;
      try {
        const params = {
          page: this.page,
          pageSize: 20,
          ...this.appliedFilter,
        };
        const { data } = await ApiService.query('v1/invoice-adjustment-drafts', { params });
        const result = data.data || data;
        this.drafts = result.items || [];
        this.totalItems = result.total || 0;
        this.totalPages = Math.ceil(this.totalItems / 20) || 1;
      } catch (err) {
        makeToastFaile(err?.response?.data?.message || 'Lỗi tải danh sách nháp.');
      } finally {
        this.isLoading = false;
      }
    },
    viewDetail(d) {
      this.$router.push({ name: 'adjustment-draft-detail', params: { draftId: d.id } });
    },
    goToRefundBills() {
      this.$router.push({ name: 'list-refund-bill' });
    },
    async confirmDelete(d) {
      const result = await Swal.fire({
        title: 'Xóa nháp?',
        text: `Xóa nháp ${this.draftShortLabel(d)}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: 'Xóa',
        cancelButtonText: '<span style="color:#464E5F">Hủy</span>',
        reverseButtons: true,
      });
      if (result.value) {
        await this.deleteDraft(d);
      }
    },
    async deleteDraft(d) {
      try {
        await ApiService.delete(`v1/invoice-adjustment-drafts/${d.id}`);
        makeToastSuccess('Xóa nháp thành công.');
        this.fetchList();
      } catch (err) {
        makeToastFaile(err?.response?.data?.message || 'Xóa thất bại.');
      }
    },
    async confirmIssue(d) {
      const result = await Swal.fire({
        title: 'Phát hành hóa đơn điều chỉnh?',
        html: `<p>Phát hành hoá đơn <strong>${d.returnBillNumber || '#' + d.id}</strong>?</p><p class="text-warning">Lưu ý! Thao tác này không thể hoàn tác.</p>`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#1BC5BD',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: 'Phát hành',
        cancelButtonText: '<span style="color:#464E5F">Hủy</span>',
        reverseButtons: true,
      });
      if (result.value) {
        await this.issueDraft(d);
      }
    },
    async issueDraft(d) {
      try {
        await ApiService.post(`v1/invoice-adjustment-drafts/${d.id}/issue`);
        makeToastSuccess('Phát hành thành công.');
        this.fetchList();
      } catch (err) {
        makeToastFaile(err?.response?.data?.message || 'Phát hành thất bại.');
      }
    },
    async confirmRetry(d) {
      try {
        await ApiService.post(`v1/invoice-adjustment-drafts/${d.id}/retry`);
        makeToastSuccess('Đã gửi lại yêu cầu phát hành.');
        this.fetchList();
      } catch (err) {
        makeToastFaile(err?.response?.data?.message || 'Retry thất bại.');
      }
    },
    async openPdfViewer(item) {
      try {
        await this.$refs.pdfViewer.open(buildAdjustmentDraftPdfViewerConfig(item));
      } catch (err) {
        makeToastFaile(err?.message || 'Không thể mở PDF hoá đơn');
      }
    },
    async refreshTaxStatus(item) {
      try {
        await ApiService.post(`v1/invoice-adjustment-drafts/${item.id}/query-tax-status`);
        makeToastSuccess('Đã cập nhật trạng thái CQT');
        this.fetchList();
      } catch (err) {
        makeToastFaile(err?.response?.data?.message || 'Kiểm tra CQT thất bại');
      }
    },
    triggerBulkIssue() {
      if (!this.hasSelection) return;
      const rows = this.selectedRows.filter((r) => this.isRowSelectableForBulk(r));
      if (!rows.length) {
        makeToastFaile('Không có nháp đủ điều kiện phát hành trong danh sách đã chọn.');
        return;
      }
      this.$refs.adjustmentBulkIssueModal.open(rows);
    },
    onBulkIssueClosed() {
      this.clearSelection();
      this.fetchList();
    },
    async triggerBulkDelete() {
      if (!this.hasSelection) return;
      const rows = this.selectedRows.filter((r) => r?.actions?.canDelete);
      if (!rows.length) {
        makeToastFaile('Không có nháp được phép xóa trong danh sách đã chọn.');
        return;
      }
      const result = await Swal.fire({
        title: `Xóa ${rows.length} nháp?`,
        text: 'Chỉ nháp ở trạng thái Nháp hoặc Lỗi phát hành mới được xóa.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: 'Xóa',
        cancelButtonText: '<span style="color:#464E5F">Hủy</span>',
        reverseButtons: true,
      });
      if (!result.value) return;
      this.isBulkDeleteProcessing = true;
      try {
        let ok = 0;
        for (let i = 0; i < rows.length; i += 1) {
          const d = rows[i];
          try {
            await ApiService.delete(`v1/invoice-adjustment-drafts/${d.id}`);
            ok += 1;
          } catch (err) {
            makeToastFaile(
              err?.response?.data?.message ||
                `Xóa thất bại: ${this.draftShortLabel(d)}`,
            );
            break;
          }
        }
        if (ok) {
          makeToastSuccess(`Đã xóa ${ok} nháp.`);
        }
        this.clearSelection();
        await this.fetchList();
      } finally {
        this.isBulkDeleteProcessing = false;
      }
    },
    /** HĐ trả hàng — cùng pattern refunds/List.vue */
    getReturnBillDetailHref(item) {
      const billNumber = item?.returnBillNumber ?? item?.returnBillId;
      if (billNumber == null || String(billNumber).trim() === '') return null;
      const { href } = this.$router.resolve({
        name: 'update-refund-bill',
        query: { billNumber: String(billNumber).trim() },
      });
      return href || null;
    },
    /** HĐ gốc (bán lẻ / bán buôn) — cùng ý refunds/List linkToBill + einvoice List saleInvoiceRef */
    getOriginalBillDetailHref(item) {
      const id = item?.originalBillNumber ?? item?.originalBillId;
      if (id == null || String(id).trim() === '') return null;
      const isWholesale = item.sourceType === 'WHOLESALE';
      const name = isWholesale ? 'update-wholesale-invoice' : 'update-bill';
      const { href } = this.$router.resolve({
        name,
        query: { id: String(id).trim() },
      });
      return href || null;
    },
    /** HĐĐT gốc — Detail.vue (salesInvoiceId = originalBillId) */
    resolveOriginalEinvoiceSaleType(item) {
      const sourceType = String(item?.sourceType || '')
        .trim()
        .toUpperCase();
      if (sourceType === 'WHOLESALE') return EINVOICE_SALE_TYPE.WHOLESALE;
      if (sourceType) return EINVOICE_SALE_TYPE.RETAIL;
      if (item?.providerFlow === 'PORTAL') return EINVOICE_SALE_TYPE.WHOLESALE;
      if (item?.providerFlow === 'POS') return EINVOICE_SALE_TYPE.RETAIL;
      return null;
    },
    getOriginalEinvoiceDetailHref(item) {
      const salesInvoiceId = item?.originalBillId;
      if (salesInvoiceId == null || String(salesInvoiceId).trim() === '') return null;
      const saleType = this.resolveOriginalEinvoiceSaleType(item);
      if (!saleType) return null;
      const { href } = this.$router.resolve({
        name: 'einvoice-detail',
        params: { salesInvoiceId: String(salesInvoiceId).trim() },
        query: { saleType },
      });
      return href || null;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../einvoice.scss';

.adjustment-draft-list {
  .einvoice-filter {
    &__label {
      display: block;
      margin-bottom: 0.35rem;
      font-size: 0.75rem;
      font-weight: 600;
      color: #5e6278;
      text-transform: uppercase;
      letter-spacing: 0.02em;
    }

    &__actions {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
      min-height: 31px;
    }
  }

  .adjustment-draft-btable {
    ::v-deep table.adjustment-draft-list__table {
      table-layout: fixed;
      width: 100%;
    }
  }

  .einvoice-table {
    font-size: 0.875rem;

    ::v-deep {
      th {
        vertical-align: middle;
        background-color: #f3f6f9;
        font-size: 0.8rem;
        font-weight: 600;
        color: #464e5f;
        letter-spacing: 0.02em;
        text-transform: uppercase;
      }
      td {
        vertical-align: middle;
        padding: 0.5rem 0.75rem;
      }
      .cursor-pointer {
        cursor: pointer;
      }
      tr:hover {
        background-color: #f3f6f9 !important;
      }
    }
  }

  .adjustment-draft-col-head-split {
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1.2;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }

  .adjustment-draft-col-head-split .text-muted {
    font-size: 0.68rem;
    margin-top: 0.1rem;
  }

  .adjustment-draft-invoice-cell {
    max-width: 140px;
    margin-left: auto;
    margin-right: auto;
    word-break: break-word;
    overflow-wrap: anywhere;
  }

  .adjustment-draft-invoice-link {
    font-size: 0.9375rem;
    line-height: 1.25;
    font-variant-numeric: tabular-nums;
  }

  .einvoice-source-link {
    display: inline-flex;
    align-items: center;
    font-weight: 700;
    color: #3699ff;
    text-decoration: none;
    transition: color 0.15s ease;

    i {
      font-size: 0.65rem;
      opacity: 0.6;
      transition: opacity 0.15s ease;
    }

    &:hover {
      color: #1e6fd9;
      text-decoration: underline;

      i {
        opacity: 1;
      }
    }
  }
}
</style>

<style lang="scss">
.adjustment-draft-list .einvoice-filter {
  .bootstrap-datetimepicker-widget {
    z-index: 1055 !important;
    position: absolute !important;
  }

  .input-group {
    flex-wrap: nowrap;
  }
}
</style>
